import { useEffect, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import DietaryMark from "@/components/DietaryMark";
import PageSeo from "@/components/PageSeo";

const COLLAPSE_STORAGE_KEY = "menu-collapsed-sections-v1";

export default function Menu() {
  const [tab, setTab] = useState("alacarte");
  const [dietFilter, setDietFilter] = useState("all"); // all | veg | non-veg
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Section collapse state. On mobile, everything after the first is collapsed by default.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [mobileInitDone, setMobileInitDone] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/menu-items").then((r) => setItems(r.data)).catch(() => {}),
      api.get("/party-packages").then((r) => setPackages(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Restore explicit user preference from localStorage on mount.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLLAPSE_STORAGE_KEY) || "null");
      if (Array.isArray(saved)) setCollapsed(new Set(saved));
    } catch { /* ignore */ }
  }, []);

  // On mobile (<768px) and first load of items, collapse every section except the first
  // — but only if the user hasn't touched the toggle yet (mobileInitDone flag).
  useEffect(() => {
    if (mobileInitDone) return;
    if (loading) return;
    if (items.length === 0) return;
    if (typeof window === "undefined" || window.innerWidth >= 768) {
      setMobileInitDone(true);
      return;
    }
    const savedRaw = localStorage.getItem(COLLAPSE_STORAGE_KEY);
    if (savedRaw) { setMobileInitDone(true); return; }  // respect existing preference
    // Group sections by first-seen order like `grouped` does — collapse all but first.
    const sectionsInOrder = [];
    for (const it of items) if (!sectionsInOrder.includes(it.section)) sectionsInOrder.push(it.section);
    setCollapsed(new Set(sectionsInOrder.slice(1)));
    setMobileInitDone(true);
  }, [loading, items, mobileInitDone]);

  const toggleSection = (name) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  };

  const setAllCollapsed = (collapse) => {
    if (!collapse) {
      setCollapsed(new Set());
      try { localStorage.setItem(COLLAPSE_STORAGE_KEY, "[]"); } catch { /* ignore */ }
      return;
    }
    const allNames = grouped.map((s) => s.name);
    setCollapsed(new Set(allNames));
    try { localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify(allNames)); } catch { /* ignore */ }
  };

  // Group menu items by section, preserving first-seen order — apply diet filter, drop empty sections
  const grouped = useMemo(() => {
    const filtered = items.filter((it) => {
      if (dietFilter === "veg") return it.dietary === "veg";
      if (dietFilter === "non-veg") return it.dietary === "non-veg";
      return true;
    });
    const map = new Map();
    for (const it of filtered) {
      if (!map.has(it.section)) map.set(it.section, []);
      map.get(it.section).push(it);
    }
    return Array.from(map.entries()).map(([name, list]) => ({ name, items: list }));
  }, [items, dietFilter]);

  const vegCount = useMemo(() => items.filter((i) => i.dietary === "veg").length, [items]);
  const nonVegCount = useMemo(() => items.filter((i) => i.dietary === "non-veg").length, [items]);

  return (
    <div data-testid="menu-page" className="pt-32 pb-24">
      <PageSeo
        title="Menu — The Barracks Bistro (À la carte & Party menu)"
        description="Wood-fired flatbread, Nilgiri mushrooms, lamb shank, and full party packages from ₹799. Veg and non-veg tagged, prices in ₹."
        path="/menu"
      />
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="eyebrow">The Menu</div>
        <h1 className="mt-3 text-5xl sm:text-6xl">
          What we're cooking <span className="italic text-[hsl(var(--brass))]">this week.</span>
        </h1>

        <div className="mt-10 inline-flex border border-[hsl(var(--rule))] rounded-full p-1 bg-[hsl(var(--bg-2))]" role="tablist">
          <button
            data-testid="tab-alacarte" role="tab" aria-selected={tab === "alacarte"} onClick={() => setTab("alacarte")}
            className={`px-5 py-2.5 text-sm rounded-full transition-all ${tab === "alacarte" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
          >À la carte</button>
          <button
            data-testid="tab-party" role="tab" aria-selected={tab === "party"} onClick={() => setTab("party")}
            className={`px-5 py-2.5 text-sm rounded-full transition-all ${tab === "party" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"}`}
          >Party menu</button>
        </div>

        {tab === "alacarte" && (
          <div data-testid="menu-alacarte" className="mt-12">
            <p className="text-[hsl(var(--ink-soft))] max-w-2xl">
              Our menu shifts with the market. Below is a snapshot — expect one or two changes when
              you arrive, all for the better. Full wine and dessert lists at your table.
            </p>

            <div className="mt-6 flex items-center gap-3 flex-wrap" data-testid="menu-legend">
              <div className="inline-flex border border-[hsl(var(--rule))] rounded-full p-1 bg-[hsl(var(--bg-2))]" role="tablist" aria-label="Dietary filter">
                <button
                  data-testid="filter-all"
                  onClick={() => setDietFilter("all")}
                  aria-pressed={dietFilter === "all"}
                  className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-full transition-all ${
                    dietFilter === "all" ? "bg-[hsl(var(--forest))] text-[hsl(var(--bg))]" : "text-[hsl(var(--ink-soft))]"
                  }`}
                >
                  All
                </button>
                <button
                  data-testid="filter-veg"
                  onClick={() => setDietFilter("veg")}
                  aria-pressed={dietFilter === "veg"}
                  className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-full transition-all inline-flex items-center gap-1.5 ${
                    dietFilter === "veg" ? "bg-[hsl(148_55%_34%)] text-white" : "text-[hsl(var(--ink-soft))]"
                  }`}
                >
                  <DietaryMark value="veg" /> Veg only <span className="opacity-70">· {vegCount}</span>
                </button>
                <button
                  data-testid="filter-non-veg"
                  onClick={() => setDietFilter("non-veg")}
                  aria-pressed={dietFilter === "non-veg"}
                  className={`px-4 py-1.5 text-xs uppercase tracking-wider rounded-full transition-all inline-flex items-center gap-1.5 ${
                    dietFilter === "non-veg" ? "bg-[hsl(6_68%_45%)] text-white" : "text-[hsl(var(--ink-soft))]"
                  }`}
                >
                  <DietaryMark value="non-veg" /> Non-veg <span className="opacity-70">· {nonVegCount}</span>
                </button>
              </div>
            </div>

            {loading && (<div className="mt-14 text-[hsl(var(--ink-soft))]">Loading menu...</div>)}

            {!loading && grouped.length === 0 && (
              <div className="mt-14 text-[hsl(var(--ink-soft))]" data-testid="menu-empty">
                {dietFilter === "veg" && "No vegetarian dishes match right now."}
                {dietFilter === "non-veg" && "No non-vegetarian dishes match right now."}
                {dietFilter === "all" && "Menu is being updated. Please check back shortly."}
              </div>
            )}

            <div className="mt-14 space-y-8">
              <div className="flex items-center justify-end gap-3 text-xs text-[hsl(var(--ink-soft))]">
                <button
                  data-testid="menu-expand-all"
                  onClick={() => setAllCollapsed(false)}
                  className="hover:text-[hsl(var(--forest))] uppercase tracking-wider"
                >
                  Expand all
                </button>
                <span aria-hidden>·</span>
                <button
                  data-testid="menu-collapse-all"
                  onClick={() => setAllCollapsed(true)}
                  className="hover:text-[hsl(var(--forest))] uppercase tracking-wider"
                >
                  Collapse all
                </button>
              </div>
              {grouped.map((s) => {
                const isCollapsed = collapsed.has(s.name);
                return (
                  <div key={s.name} data-testid={`menu-section-${s.name.split(" ")[0].toLowerCase()}`}>
                    <button
                      type="button"
                      data-testid={`menu-section-toggle-${s.name.split(" ")[0].toLowerCase()}`}
                      onClick={() => toggleSection(s.name)}
                      aria-expanded={!isCollapsed}
                      className="w-full flex items-baseline justify-between border-b border-[hsl(var(--rule))] pb-3 group cursor-pointer text-left"
                    >
                      <h2 className="text-3xl group-hover:text-[hsl(var(--brass-2))] transition-colors">{s.name}</h2>
                      <div className="flex items-baseline gap-3 text-xs text-[hsl(var(--ink-soft))] uppercase tracking-wider">
                        <span>{s.items.length} {s.items.length === 1 ? "dish" : "dishes"}</span>
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${isCollapsed ? "-rotate-90" : "rotate-0"}`}
                        />
                      </div>
                    </button>
                    {!isCollapsed && (
                      <ul className="mt-6 divide-y divide-[hsl(var(--rule))]">
                        {s.items.map((it) => (
                          <li key={it.id} data-testid={`menu-item-${it.id}`} className="py-5 flex items-baseline justify-between gap-6">
                            <div className="flex items-baseline gap-3 min-w-0">
                              <DietaryMark value={it.dietary} className="translate-y-[2px]" />
                              <div className="min-w-0">
                                <div className="text-lg">{it.name}</div>
                                {it.description && <div className="text-sm text-[hsl(var(--ink-soft))] mt-1">{it.description}</div>}
                              </div>
                            </div>
                            <div className="text-[hsl(var(--forest))] font-semibold whitespace-nowrap">{it.price}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tab === "party" && (
          <div data-testid="menu-party" className="mt-12">
            <p className="text-[hsl(var(--ink-soft))] max-w-2xl">
              Set menus for private functions, birthdays, and small celebrations — per person,
              inclusive of soup, starters, mains, breads, salad and dessert. Minimum 20 pax.
            </p>

            {packages.length === 0 ? (
              <div className="mt-12 text-[hsl(var(--ink-soft))]">Loading packages...</div>
            ) : (
              <div className={`mt-12 grid grid-cols-1 gap-6 ${packages.length >= 3 ? "lg:grid-cols-3" : packages.length === 2 ? "md:grid-cols-2" : ""}`}>
                {packages.map((pkg) => (
                  <article key={pkg.id} data-testid={`party-package-${pkg.price}`} className="bg-[hsl(var(--bg-2))] p-8 relative border border-[hsl(var(--rule))]">
                    <div className="eyebrow">{pkg.tagline}</div>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="display text-5xl text-[hsl(var(--forest))]">₹{pkg.price}</span>
                      <span className="text-sm text-[hsl(var(--ink-soft))]">/ pax</span>
                    </div>
                    <div className="rule-brass my-5" />
                    <div className="space-y-4">
                      {pkg.sections.map((sec) => (
                        <div key={sec.name}>
                          <div className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--brass-2))] font-semibold">{sec.name}</div>
                          <ul className="mt-1.5 text-sm text-[hsl(var(--ink))] space-y-1">
                            {sec.items.map((it) => {
                              const name = typeof it === "string" ? it : it.name;
                              const diet = typeof it === "string" ? "" : it.dietary;
                              return (
                                <li key={name} className="flex items-baseline gap-2">
                                  <DietaryMark value={diet} className="translate-y-[2px]" />
                                  <span>{name}</span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </article>
                ))}
              </div>
            )}

            <div className="mt-12 text-sm text-[hsl(var(--ink-soft))] text-center">
              Prices in INR, exclusive of taxes. Vegetarian only options available on request. For
              custom menus and larger events, please{" "}
              <a href="/reservations" className="text-[hsl(var(--forest))] underline underline-offset-4">reach out</a>.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
